import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supportTicketsAPI } from '../../services/portalAPI';

const categories = [
  { value: 'question', label: 'Question' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'billing', label: 'Billing' },
  { value: 'other', label: 'Other' },
];

const PortalNewTicket: React.FC = () => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('question');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      setError('Subject and description are required.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('subject', subject.trim());
      formData.append('category', category);
      formData.append('description', description.trim());
      if (file) {
        formData.append('screenshot', file);
      }

      await supportTicketsAPI.create(formData);
      navigate('/portal/support');
    } catch (err) {
      console.error('Failed to create ticket', err);
      setError('Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: 6,
    fontSize: 14, boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 6, color: '#333',
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 16px' }}>
      <button
        onClick={() => navigate('/portal/support')}
        style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: 14, padding: 0, marginBottom: 16 }}
      >
        &larr; Back to Support
      </button>

      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>New Support Ticket</h1>

      {error && (
        <div style={{ background: '#fdecea', color: '#c62828', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Brief summary of your issue"
            style={inputStyle}
            maxLength={255}
          />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Category</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            style={inputStyle}
          >
            {categories.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Describe the issue in detail. Include steps to reproduce if reporting a bug."
            rows={6}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Attachment (optional)</label>
          <input
            type="file"
            onChange={e => setFile(e.target.files?.[0] || null)}
            accept="image/*,.pdf,.txt,.doc,.docx"
            style={{ fontSize: 14 }}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            background: submitting ? '#90caf9' : '#1976d2', color: '#fff', border: 'none',
            borderRadius: 6, padding: '12px 28px', fontSize: 15, fontWeight: 600,
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Ticket'}
        </button>
      </form>
    </div>
  );
};

export default PortalNewTicket;
